import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Zap, Calculator, BarChart3, ChevronDown } from 'lucide-react';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  sportsbookIcons,
  sportsbookNames,
  formatMarketType,
  formatOdds,
  formatGameTime,
  getOddsFormat,
  setOddsFormat as saveOddsFormat
} from '../utils/oddsUtils';
import DataBanner from './DataBanner';
import FilterBar from './FilterBar';
import MarketBadge from './MarketBadge';

function Arbitrage() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultBetSize, setDefaultBetSize] = useState(100);
  const [showWhatIs, setShowWhatIs] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [oddsFormat, setOddsFormat] = useState(getOddsFormat());
  const { getToken } = useAuth();
  const { user } = useUser();
  const myBooks = user?.unsafeMetadata?.selectedBooks || [];

  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [selectedKeytypes, setSelectedKeytypes] = useState([]);
  const [dateLimit, setDateLimit] = useState(null);
  const [showOnlyMyBooks, setShowOnlyMyBooks] = useState(false);

  const availableLeagues = useMemo(() =>
    [...new Set(opportunities.map(o => o.sporttitle))].sort(),
    [opportunities]
  );
  const availableBooks = useMemo(() => {
    const books = new Set();
    opportunities.forEach(o => {
      if (o.bookmakerid1) books.add(o.bookmakerid1);
      if (o.bookmakerid2) books.add(o.bookmakerid2);
    });
    return [...books].sort((a, b) => (sportsbookNames[a] || a).localeCompare(sportsbookNames[b] || b));
  }, [opportunities]);
  const availableKeytypes = useMemo(() =>
    [...new Set(opportunities.map(o => o.keytype))].sort(),
    [opportunities]
  );

  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(opp => {
      if (dateLimit !== null) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() + dateLimit);
        cutoff.setHours(23, 59, 59, 999);
        if (new Date(opp.commencetime) > cutoff) return false;
      }
      if (selectedLeagues.length > 0 && !selectedLeagues.includes(opp.sporttitle)) return false;
      if (selectedBooks.length > 0 && !selectedBooks.includes(opp.bookmakerid1) && !selectedBooks.includes(opp.bookmakerid2)) return false;
      if (showOnlyMyBooks && myBooks.length > 0 && (!myBooks.includes(opp.bookmakerid1) || !myBooks.includes(opp.bookmakerid2))) return false;
      if (selectedKeytypes.length > 0 && !selectedKeytypes.includes(opp.keytype)) return false;
      return true;
    });
  }, [opportunities, selectedLeagues, selectedBooks, selectedKeytypes, dateLimit, showOnlyMyBooks, myBooks]);

  const handleOddsFormatChange = (format) => {
    setOddsFormat(format);
    saveOddsFormat(format);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/arbitrage`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOpportunities(response.data);
      } catch (error) {
        console.error('Error fetching arbitrage:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getToken]);

  if (loading) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0a0e1a'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #2d3748',
            borderTop: '4px solid #00d9ff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: '#64748b', fontSize: '16px' }}>Loading arbitrage opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#0a0e1a',
      minHeight: '100vh',
      padding: '40px 60px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '800',
            margin: 0,
            color: '#ffffff',
            letterSpacing: '-0.5px'
          }}>
            Arbitrage
          </h1>

          {/* Odds Format Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'American' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              American
            </span>
            <div
              onClick={() => handleOddsFormatChange(oddsFormat === 'American' ? 'Decimal' : 'American')}
              style={{
                width: '48px',
                height: '26px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #2d3748',
                borderRadius: '13px',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#00d9ff',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: oddsFormat === 'American' ? '3px' : '23px',
                transition: 'left 0.2s ease'
              }} />
            </div>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'Decimal' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              Decimal
            </span>
          </div>
        </div>

        <DataBanner />

        {/* Description Section */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '10px'
        }}>
          <div
            onClick={() => setShowWhatIs(!showWhatIs)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              What is Arbitrage Betting?
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showWhatIs ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showWhatIs && (
            <div style={{ marginTop: '15px' }}>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 15px 0'
              }}>
                In simplest terms, a bettor places wagers on two sides of a bet where the profit from either
                outcome outweighs the loss from the other side.
              </p>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 15px 0'
              }}>
                Arbitrage betting involves placing bets on all possible outcomes of an event across different
                sportsbooks to take advantage of pricing discrepancies. By carefully calculating stake sizes,
                bettors create small profit opportunities. This approach requires quick execution and an
                understanding of sportsbook limits, odds movement, and potential account restrictions.
              </p>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 20px 0'
              }}>
                For two-sided bets using American odds, if the <span style={{ color: '#10b981', fontWeight: '600' }}>+odds</span> are
                higher than the <span style={{ color: '#10b981', fontWeight: '600' }}>−odds</span>, that's an arbitrage opportunity.
                Then you just have to balance each wager correctly.
              </p>

              {/* Example Box */}
              <div style={{
                backgroundColor: '#0f1419',
                border: '1px solid #2d3748',
                borderRadius: '10px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '15px'
                }}>
                  Example: Finding Arbitrage
                </div>

                {/* Odds Comparison */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '15px',
                  marginBottom: '15px'
                }}>
                  <div style={{
                    backgroundColor: '#1a1f2e',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #2d3748',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff', marginBottom: '10px' }}>Sportsbook A</div>
                    <div style={{ color: '#a0aec0', fontSize: '14px' }}>
                      Home <span style={{
                        fontFamily: 'monospace',
                        fontWeight: '600',
                        color: '#00d9ff',
                        border: '1px solid #00d9ff',
                        borderRadius: '12px',
                        padding: '2px 8px'
                      }}>+200</span>
                      <span style={{ color: '#64748b', margin: '0 12px' }}>vs.</span>
                      Away <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>−225</span>
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: '#1a1f2e',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #2d3748',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#ffffff', marginBottom: '10px' }}>Sportsbook B</div>
                    <div style={{ color: '#a0aec0', fontSize: '14px' }}>
                      Home <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>+165</span>
                      <span style={{ color: '#64748b', margin: '0 12px' }}>vs.</span>
                      Away <span style={{
                        fontFamily: 'monospace',
                        fontWeight: '600',
                        color: '#00d9ff',
                        border: '1px solid #00d9ff',
                        borderRadius: '12px',
                        padding: '2px 8px'
                      }}>−190</span>
                    </div>
                  </div>
                </div>

                {/* Best Odds */}
                <div style={{
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '8px',
                  padding: '12px 15px',
                  textAlign: 'center'
                }}>
                  <span style={{ color: '#a0aec0', fontSize: '14px' }}>Best Odds: </span>
                  <span style={{ color: '#10b981', fontFamily: 'monospace', fontWeight: '700', fontSize: '15px' }}>Home +200</span>
                  <span style={{ color: '#64748b', margin: '0 10px' }}>vs</span>
                  <span style={{ color: '#10b981', fontFamily: 'monospace', fontWeight: '700', fontSize: '15px' }}>Away −190</span>
                  <span style={{ color: '#64748b', fontSize: '13px', marginLeft: '15px' }}>(200 {'>'} 190 = Arbitrage!)</span>
                </div>
              </div>

              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 20px 0'
              }}>
                Once you've found an arbitrage, you can balance your bets in different ways depending on which
                outcome you want to favor:
              </p>

              {/* Three Scenarios */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '15px'
              }}>
                {/* Bias Home */}
                <div style={{
                  backgroundColor: '#0f1419',
                  border: '1px solid #2d3748',
                  borderRadius: '10px',
                  padding: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#10b981',
                    marginBottom: '12px'
                  }}>
                    Bias Home
                  </div>
                  <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '8px' }}>
                    Home: <span style={{ color: '#ffffff', fontFamily: 'monospace' }}>$100</span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '12px' }}>
                    Away: <span style={{ color: '#ffffff', fontFamily: 'monospace' }}>$190</span>
                  </div>
                  <div style={{
                    borderTop: '1px solid #2d3748',
                    paddingTop: '10px',
                    fontSize: '12px',
                    color: '#64748b'
                  }}>
                    <div>If Home wins: <span style={{ color: '#10b981' }}>+$10</span></div>
                    <div>If Away wins: <span style={{ color: '#10b981' }}>+$0</span></div>
                  </div>
                </div>

                {/* Even Hedge */}
                <div style={{
                  backgroundColor: '#0f1419',
                  border: '1px solid #2d3748',
                  borderRadius: '10px',
                  padding: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#10b981',
                    marginBottom: '12px'
                  }}>
                    Even Hedge
                  </div>
                  <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '8px' }}>
                    Home: <span style={{ color: '#ffffff', fontFamily: 'monospace' }}>$100</span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '12px' }}>
                    Away: <span style={{ color: '#ffffff', fontFamily: 'monospace' }}>$196.55</span>
                  </div>
                  <div style={{
                    borderTop: '1px solid #2d3748',
                    paddingTop: '10px',
                    fontSize: '12px',
                    color: '#64748b'
                  }}>
                    <div>If Home wins: <span style={{ color: '#10b981' }}>+$3.45</span></div>
                    <div>If Away wins: <span style={{ color: '#10b981' }}>+$3.45</span></div>
                  </div>
                </div>

                {/* Bias Away */}
                <div style={{
                  backgroundColor: '#0f1419',
                  border: '1px solid #2d3748',
                  borderRadius: '10px',
                  padding: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#10b981',
                    marginBottom: '12px'
                  }}>
                    Bias Away
                  </div>
                  <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '8px' }}>
                    Home: <span style={{ color: '#ffffff', fontFamily: 'monospace' }}>$100</span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '12px' }}>
                    Away: <span style={{ color: '#ffffff', fontFamily: 'monospace' }}>$200</span>
                  </div>
                  <div style={{
                    borderTop: '1px solid #2d3748',
                    paddingTop: '10px',
                    fontSize: '12px',
                    color: '#64748b'
                  }}>
                    <div>If Home wins: <span style={{ color: '#10b981' }}>+$0</span></div>
                    <div>If Away wins: <span style={{ color: '#10b981' }}>+$5.26</span></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '20px'
        }}>
          <div
            onClick={() => setShowHowTo(!showHowTo)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              How to Use
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showHowTo ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showHowTo && (
            <div style={{
              fontSize: '15px',
              lineHeight: '1.8',
              color: '#a0aec0',
              marginTop: '15px'
            }}>
              <p style={{ margin: '0 0 10px 0' }}>
                1. Set your desired bet amount using the "Bet Size" input
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                2. Review the opportunities' sportbooks, profit percentage, and suggested bet size
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                3. Use the calculator (<Calculator size={20} color="#a0aec0" />) for detailed payout scenarios and verification
              </p>
              <p style={{ margin: 0 }}>
                4. Place bets on both outcomes at the specified sportsbooks
              </p>
            </div>
          )}
        </div>

        {/* Opportunities Table */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '20px'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '15px',
            borderBottom: '1px solid #2d3748'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)',
                border: '1px solid rgba(0, 217, 255, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Zap size={16} color="#00d9ff" />
              </div>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#ffffff'
              }}>
                Live Opportunities
              </h2>
              <div style={{
                padding: '4px 12px',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#10b981'
              }}>
                {filteredOpportunities.length} ACTIVE
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#a0aec0'
              }}>
                Bet Size:
              </label>
              <input
                type="number"
                value={defaultBetSize}
                onChange={(e) => setDefaultBetSize(e.target.value)}
                style={{
                  padding: '8px 12px',
                  width: '100px',
                  fontSize: '14px',
                  fontWeight: '600',
                  fontFamily: 'monospace',
                  backgroundColor: '#0f1419',
                  border: '1px solid #2d3748',
                  borderRadius: '6px',
                  color: '#ffffff',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    setDefaultBetSize(100);
                  }
                  e.target.style.borderColor = '#2d3748';
                }}
              />
            </div>
          </div>

          <FilterBar
            availableLeagues={availableLeagues}
            availableBooks={availableBooks}
            availableKeytypes={availableKeytypes}
            selectedLeagues={selectedLeagues}
            selectedBooks={selectedBooks}
            selectedKeytypes={selectedKeytypes}
            dateLimit={dateLimit}
            setSelectedLeagues={setSelectedLeagues}
            setSelectedBooks={setSelectedBooks}
            setSelectedKeytypes={setSelectedKeytypes}
            setDateLimit={setDateLimit}
            showOnlyMyBooks={showOnlyMyBooks}
            setShowOnlyMyBooks={setShowOnlyMyBooks}
          />

          {/* Column Headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '80px 1.5fr 140px 1fr 1fr 50px',
            gap: '16px',
            alignItems: 'center',
            padding: '0 20px 12px 20px',
            borderBottom: '1px solid #2d3748',
            marginBottom: '12px'
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              % Profit
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Event
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Market
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Side A
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Side B
            </div>
            <div></div>
          </div>

          {/* Opportunities List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filteredOpportunities.map((opp, index) => {
              const profitPercent = (opp.arb_percent_return * 100).toFixed(2);

              return (
                <div key={index} style={{
                  backgroundColor: '#0f1419',
                  border: '1px solid #2d3748',
                  borderRadius: '8px',
                  padding: '12px 20px',
                  transition: 'all 0.2s',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#00d9ff';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 217, 255, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#2d3748';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 1.5fr 140px 1fr 1fr 50px',
                    gap: '16px',
                    alignItems: 'center'
                  }}>
                    {/* Profit % */}
                    <div style={{
                      fontSize: '22px',
                      fontWeight: '800',
                      fontFamily: 'monospace',
                      color: '#10b981',
                      textAlign: 'center'
                    }}>
                      {profitPercent}%
                    </div>

                    {/* Event (Matchup + League + Time) */}
                    <div>
                      <div style={{
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#ffffff',
                        marginBottom: '4px'
                      }}>
                        {opp.home_team} vs. {opp.away_team}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#64748b'
                      }}>
                        {opp.sporttitle} - {formatGameTime(opp.commencetime)}
                      </div>
                    </div>

                    {/* Market Type */}
                    <MarketBadge keytype={opp.keytype} playerName={opp.player_name} />

                    {/* Side A */}
                    <div style={{
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #2d3748',
                      borderRadius: '8px',
                      padding: '12px'
                    }}>
                      <div style={{
                        fontWeight: '600',
                        fontSize: '13px',
                        color: '#ffffff',
                        marginBottom: '6px'
                      }}>
                        {opp.name1} {opp.point1 ? `(${opp.point1})` : ''}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <img
                            src={`/sportsbook-icons/${sportsbookIcons[opp.bookmakerid1] || 'placeholder.png'}`}
                            alt={sportsbookNames[opp.bookmakerid1] || opp.bookmakerid1}
                            title={sportsbookNames[opp.bookmakerid1] || opp.bookmakerid1}
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              objectFit: 'contain'
                            }}
                          />
                          <span style={{
                            color: '#00d9ff',
                            fontFamily: 'monospace',
                            fontWeight: '600',
                            fontSize: '13px'
                          }}>
                            {formatOdds(opp.price1, oddsFormat)}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '15px',
                          color: '#00d9ff',
                          fontWeight: '700',
                          fontFamily: 'monospace'
                        }}>
                          ${Number(defaultBetSize)}
                        </div>
                      </div>
                    </div>

                    {/* Side B */}
                    <div style={{
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #2d3748',
                      borderRadius: '8px',
                      padding: '12px'
                    }}>
                      <div style={{
                        fontWeight: '600',
                        fontSize: '13px',
                        color: '#ffffff',
                        marginBottom: '6px'
                      }}>
                        {opp.name2} {opp.point2 ? `(${opp.point2})` : ''}
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <img
                            src={`/sportsbook-icons/${sportsbookIcons[opp.bookmakerid2] || 'placeholder.png'}`}
                            alt={sportsbookNames[opp.bookmakerid2] || opp.bookmakerid2}
                            title={sportsbookNames[opp.bookmakerid2] || opp.bookmakerid2}
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              objectFit: 'contain'
                            }}
                          />
                          <span style={{
                            color: '#00d9ff',
                            fontFamily: 'monospace',
                            fontWeight: '600',
                            fontSize: '13px'
                          }}>
                            {formatOdds(opp.price2, oddsFormat)}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '15px',
                          color: '#00d9ff',
                          fontWeight: '700',
                          fontFamily: 'monospace'
                        }}>
                          ${Math.round(Number(defaultBetSize) * opp.bet_multiplier)}
                        </div>
                      </div>
                    </div>

                    {/* Calculator Button */}
                    <Link
                      to="/arbitrage-calculator"
                      state={{
                        oddsA: opp.price1,
                        oddsB: opp.price2,
                        betA: Number(defaultBetSize),
                        betB: Math.round(Number(defaultBetSize) * opp.bet_multiplier)
                      }}
                      style={{ textDecoration: 'none' }}
                    >
                      <button style={{
                        width: '40px',
                        height: '40px',
                        padding: 0,
                        cursor: 'pointer',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #2d3748',
                        borderRadius: '8px',
                        fontSize: '20px',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#00d9ff';
                        e.currentTarget.style.borderColor = '#00d9ff';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#1a1f2e';
                        e.currentTarget.style.borderColor = '#2d3748';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                      >
                        <Calculator size={18} color="#a0aec0" />
                      </button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredOpportunities.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px',
              color: '#64748b'
            }}>
              <BarChart3 size={48} color="#64748b" style={{ marginBottom: '20px' }} />
              <p style={{ fontSize: '16px', margin: 0 }}>
                No arbitrage opportunities found at the moment. Check back soon!
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default Arbitrage;
